import { Warning, Rocket } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

export default function QuotaBanner({ user }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const q = user?.quota;
  if (!q || dismissed) return null;

  const isTrial = q.plan_code === "trial";
  const used = q.tests_used || 0;
  const limit = q.tests_limit || 0;
  const pct = limit > 0 ? used / limit : 0;

  // Trial exhausted
  if (isTrial && used >= limit) {
    return (
      <BannerRow
        tone="red"
        testid="quota-banner-trial-exhausted"
        icon={<Warning size={18} weight="bold" className="text-white" />}
        message="Trial esaurito. Passa a un piano a pagamento per continuare."
        cta="Scegli piano"
        onCta={() => navigate("/pricing")}
      />
    );
  }
  // Trial expiring soon
  if (isTrial && typeof q.trial_days_left === "number" && q.trial_days_left <= 3) {
    return (
      <BannerRow
        tone="amber"
        testid="quota-banner-trial-ending"
        icon={<Rocket size={18} weight="bold" className="text-white" />}
        message={`Il trial finisce tra ${q.trial_days_left} ${q.trial_days_left === 1 ? "giorno" : "giorni"}. Scegli un piano per non perdere i dati.`}
        cta="Vedi piani"
        onCta={() => navigate("/pricing")}
        onDismiss={() => setDismissed(true)}
      />
    );
  }
  // B72 TASK C: nudge 80% anche per il trial (8/10 analisi)
  if (isTrial && pct >= 0.8 && used < limit && limit > 0) {
    return (
      <BannerRow
        tone="amber-light"
        testid="quota-banner-trial-warning"
        icon={<Warning size={18} weight="bold" className="text-amber-700" />}
        message={`Hai usato ${used}/${limit} analisi del trial. Scegli un piano per continuare senza interruzioni.`}
        cta="Scegli piano"
        onCta={() => navigate("/pricing")}
        onDismiss={() => setDismissed(true)}
        darkText
      />
    );
  }
  // Helper formattazione prezzo in EUR (Italian) — fonte di verita: backend `overage_rate_cents`
  const fmtEur = (n) => `\u20ac${Number(n || 0).toFixed(2).replace(".", ",")}`;
  const overageRate = (q.overage_rate_cents || 0) / 100;  // fallback 0 se non disponibile
  // B70: Over quota +50% (overage critico) — più visibile, banner rosso
  if (!isTrial && (q.overage_count || 0) > 0 && limit > 0 && limit < 9999 && (q.overage_count / limit) >= 0.5) {
    const overageTotal = (q.overage_count || 0) * overageRate;
    return (
      <BannerRow
        tone="red"
        testid="quota-banner-overage-critical"
        icon={<Warning size={18} weight="bold" className="text-white" />}
        message={`Hai superato del 50% la quota: ${q.overage_count} analisi extra a ${fmtEur(overageRate)} saranno fatturate a fine mese (${fmtEur(overageTotal)}). Upgrade per risparmiare.`}
        cta="Cambia piano ora"
        onCta={() => navigate("/pricing")}
        onDismiss={() => setDismissed(true)}
      />
    );
  }
  // Over quota (paid): overage charging
  if (!isTrial && used >= limit && limit > 0 && limit < 9999) {
    return (
      <BannerRow
        tone="amber"
        testid="quota-banner-overage"
        icon={<Warning size={18} weight="bold" className="text-white" />}
        message={`Quota mensile superata. Ogni analisi aggiuntiva: ${fmtEur(overageRate)} in fattura a fine periodo (${q.overage_count || 0} extra finora).`}
        cta="Passa a un piano superiore"
        onCta={() => navigate("/pricing")}
        onDismiss={() => setDismissed(true)}
      />
    );
  }
  // Approaching limit (>=80%)
  if (!isTrial && pct >= 0.8 && limit < 9999) {
    return (
      <BannerRow
        tone="amber-light"
        testid="quota-banner-warning"
        icon={<Warning size={18} weight="bold" className="text-amber-700" />}
        message={`Hai usato ${Math.round(pct * 100)}% delle analisi mensili (${used}/${limit}). Considera un upgrade.`}
        cta="Vedi piani"
        onCta={() => navigate("/pricing")}
        onDismiss={() => setDismissed(true)}
        darkText
      />
    );
  }
  // Fair use soft cap (Top)
  if (q.fair_use_soft_cap && used >= q.fair_use_soft_cap) {
    return (
      <BannerRow
        tone="amber-light"
        testid="quota-banner-fairuse"
        icon={<Warning size={18} weight="bold" className="text-amber-700" />}
        message={`Hai superato la soglia fair-use di ${q.fair_use_soft_cap} analisi/mese. Se e un uso regolare, contattaci per Enterprise.`}
        cta="Parla con noi"
        onCta={() => (window.location.href = "mailto:sales@selly-hr.it")}
        onDismiss={() => setDismissed(true)}
        darkText
      />
    );
  }
  return null;
}

function BannerRow({ tone, testid, icon, message, cta, onCta, onDismiss, darkText }) {
  const bg = tone === "red" ? "bg-red-600"
    : tone === "amber" ? "bg-amber-500"
    : tone === "amber-light" ? "bg-amber-100 border border-amber-300"
    : "bg-[#1B4FD8]";
  const textCls = darkText ? "text-amber-900" : "text-white";
  const ctaCls = darkText
    ? "bg-amber-700 text-white hover:bg-amber-800"
    : "bg-white/20 hover:bg-white/30 text-white";
  return (
    <div data-testid={testid} className={`rounded-lg px-4 py-3 mb-4 flex items-center justify-between gap-3 ${bg}`}>
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        <span className={`text-sm font-medium ${textCls} truncate`}>{message}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button onClick={onCta} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${ctaCls}`}>
          {cta}
        </button>
        {onDismiss && (
          <button onClick={onDismiss} className={`p-1 rounded ${darkText ? "text-amber-700 hover:bg-amber-200" : "text-white/70 hover:bg-white/20"}`} aria-label="Chiudi">
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
